import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, X, Shield, RefreshCw, Eye, Check, Minus } from 'lucide-react';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import {
  PAGE_PERMISSIONS, FEATURE_PERMISSIONS, DATA_PERMISSIONS, ACTION_PERMISSIONS,
  WIDGET_PERMISSIONS,
  ALL_PERMISSIONS, ROLE_OPTIONS,
} from '../config/permissions';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PERM_GROUPS = [
  { id: 'pages',    label: 'Pages (Sidebar Access)',     items: PAGE_PERMISSIONS,    color: 'amber' },
  { id: 'widgets',  label: 'Dashboard Widgets',          items: WIDGET_PERMISSIONS,  color: 'purple' },
  { id: 'features', label: 'Features (Header / Widgets)',items: FEATURE_PERMISSIONS, color: 'cyan'  },
  { id: 'data',     label: 'Data Visibility',            items: DATA_PERMISSIONS,    color: 'green' },
  { id: 'actions',  label: 'Mutating Actions',           items: ACTION_PERMISSIONS,  color: 'red'   },
];

const ROLES = ROLE_OPTIONS.map(r => r.value);

const ROLE_COLORS = {
  owner:      "bg-red-900/40 text-red-300 border-red-700",
  admin:      "bg-amber-900/40 text-amber-300 border-amber-700",
  manager:    "bg-blue-900/40 text-blue-300 border-blue-700",
  sales:      "bg-purple-900/40 text-purple-300 border-purple-700",
  production: "bg-green-900/40 text-green-300 border-green-700",
  purchase:   "bg-cyan-900/40 text-cyan-300 border-cyan-700",
  accountant: "bg-zinc-700 text-zinc-300 border-zinc-600",
  staff:      "bg-zinc-800 text-zinc-500 border-zinc-700",
  client:     "bg-purple-900/30 text-purple-400 border-purple-800",
  custom:     "bg-zinc-800 text-zinc-400 border-zinc-700",
};

const emptyForm = {
  name: '', email: '', phone: '', password: '', role: 'staff',
  department: '', line: '', reports_to_email: '', google_login_email: '',
  permissions: Object.fromEntries(ALL_PERMISSIONS.map(p => [p.key, false])),
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [accessMapUserId, setAccessMapUserId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/`, { withCredentials: true });
      setUsers(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load users. You may not have permission.');
    } finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete, isAdmin } = useAdminDelete(fetchUsers);

  useEffect(() => { fetchUsers(); }, []);

  const loadRolePermissions = async (role) => {
    if (role === 'custom') return;
    try {
      const res = await axios.get(`${API}/users/role-permissions/${role}`, { withCredentials: true });
      setForm(f => ({ ...f, permissions: res.data }));
    } catch (e) { console.error(e); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name, email: u.email, phone: u.phone || '', password: '',
      role: u.role,
      department: u.department || '', line: u.line || '',
      reports_to_email: u.reports_to_email || '',
      google_login_email: u.google_login_email || '',
      permissions: { ...(u.permissions || {}) },
    });
    setError('');
    setShowModal(true);
  };

  const handleRoleChange = (role) => {
    setForm(f => ({ ...f, role }));
    loadRolePermissions(role);
  };

  const togglePerm = (permKey) => {
    setForm(f => ({ ...f, permissions: { ...f.permissions, [permKey]: !f.permissions[permKey] } }));
  };

  const setAllInGroup = (items, value) => {
    setForm(f => {
      const next = { ...f.permissions };
      items.forEach(it => { next[it.key] = value; });
      return { ...f, permissions: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (editing) {
        const res = await axios.put(`${API}/users/${editing.id}`, form, { withCredentials: true });
        setUsers(prev => prev.map(u => u.id === editing.id ? res.data : u));
      } else {
        const res = await axios.post(`${API}/users/`, form, { withCredentials: true });
        setUsers(prev => [res.data, ...prev]);
      }
      setShowModal(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error saving user');
    } finally { setSubmitting(false); }
  };

  const deleteUser = (id, name) => {
    requestDelete(`/users/${id}`, name || 'User', `Delete user "${name || 'this user'}" permanently.`);
  };

  const permCount = (u) => Object.values(u.permissions || {}).filter(Boolean).length;

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse">Loading users...</div>;

  return (
    <div className="space-y-5" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">User Management</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{users.length} team member{users.length !== 1 ? 's' : ''} · Role-based access control</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchUsers} className="p-2 rounded-xl border border-zinc-800 text-zinc-400 hover:bg-zinc-800 transition-colors"><RefreshCw size={14} /></button>
          <button data-testid="add-user-btn" onClick={openCreate} className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl transition-colors">
            <Plus size={14} /> Add User
          </button>
        </div>
      </div>

      {error && !showModal && (
        <div className="bg-red-900/20 border border-red-700 rounded-xl p-4 text-red-300 text-sm" data-testid="users-error">{error}</div>
      )}

      {/* Users Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Name', 'Email', 'Role', 'Access', 'Created', ''].map(h => (
                <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <React.Fragment key={u.id}>
              <tr className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-red-600/20 border border-red-600/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-400 text-xs font-bold">{u.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <span className="text-zinc-200 text-xs font-medium">{u.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-400 text-xs">
                  <div>{u.email}</div>
                  {u.google_login_email && (
                    <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-emerald-300/90" data-testid={`user-google-${u.id}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Google: {u.google_login_email}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${ROLE_COLORS[u.role] || ROLE_COLORS.custom}`}>
                    {u.role?.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className="text-amber-400 font-mono font-semibold">{permCount(u)}</span>
                  <span className="text-zinc-600">/{ALL_PERMISSIONS.length}</span>
                </td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{u.created_at?.slice(0, 10) || '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button data-testid={`access-map-${u.id}`} onClick={() => setAccessMapUserId(accessMapUserId === u.id ? null : u.id)}
                      className="text-zinc-500 hover:text-sky-400 transition-colors" title="View what this user can / cannot see">
                      <Eye size={13} />
                    </button>
                    <button data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)} className="text-zinc-500 hover:text-amber-400 transition-colors"><Edit2 size={13} /></button>
                    {isAdmin && <button data-testid={`delete-user-${u.id}`} onClick={() => deleteUser(u.id, u.name)} className="text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>}
                  </div>
                </td>
              </tr>
              {accessMapUserId === u.id && (
                <tr key={`${u.id}-access`} className="bg-zinc-950/60 border-b border-zinc-800/50" data-testid={`access-map-row-${u.id}`}>
                  <td colSpan={6} className="px-4 py-4">
                    <AccessMap user={u} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          </tbody>
        </table>
        {users.length === 0 && <div className="text-center text-zinc-500 text-sm py-10">No users found</div>}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10">
              <h3 className="font-heading font-semibold text-zinc-50">{editing ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={() => setShowModal(false)}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-5" data-testid="user-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Full Name *</label>
                  <input data-testid="user-name-input" required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g. Priya Sharma" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Email *</label>
                  <input data-testid="user-email-input" required type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="priya@tirthindustries.com" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">{editing ? 'New Password (blank = keep)' : 'Password *'}</label>
                  <input data-testid="user-password-input" type="password" required={!editing} value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Role</label>
                  <select data-testid="user-role-select" value={form.role} onChange={e => handleRoleChange(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {ROLES.map(r => <option key={r} value={r} className="bg-zinc-900">{r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Google sign-in e-mail <span className="text-zinc-600">(optional)</span></label>
                  <input data-testid="user-google-email-input" type="email" value={form.google_login_email} onChange={e => setForm(f => ({...f, google_login_email: e.target.value}))} placeholder="name@gmail.com" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  <p className="text-[10px] text-zinc-500 mt-1">Only this Google account can use "Continue with Google" to sign in as this user.</p>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Phone</label>
                  <input data-testid="user-phone-input" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+91 98765 43210" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Department</label>
                  <select data-testid="user-dept-input" value={form.department} onChange={e => setForm(f => ({...f, department: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    <option value="">— Department —</option>
                    <option>QC</option>
                    <option>Production</option>
                    <option>Warehouse</option>
                    <option>Sales</option>
                    <option>Accounts</option>
                    <option>Admin</option>
                    <option>R&D</option>
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Line (if applicable)</label>
                  <select data-testid="user-line-input" value={form.line} onChange={e => setForm(f => ({...f, line: e.target.value}))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    <option value="">— None —</option>
                    <option>PL1</option>
                    <option>PL2</option>
                    <option>PL3</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Reports To (head/manager email)</label>
                  <input data-testid="user-reports-input" type="email" value={form.reports_to_email} onChange={e => setForm(f => ({...f, reports_to_email: e.target.value}))} placeholder="e.g. production.head@tirthindustries.com" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  <p className="text-[10px] text-zinc-500 mt-1">Daily work summary will be emailed to this address every 8 PM IST.</p>
                </div>
              </div>

              {/* Granular Permissions — 4 groups */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-zinc-300 text-sm font-medium flex items-center gap-2">
                    <Shield size={14} className="text-amber-400" />
                    Permissions ({Object.values(form.permissions).filter(Boolean).length} of {ALL_PERMISSIONS.length} enabled)
                  </label>
                  <div className="flex gap-2 text-xs">
                    <button type="button" onClick={() => setForm(f => ({...f, permissions: Object.fromEntries(ALL_PERMISSIONS.map(p => [p.key, true]))}))} className="text-amber-400 hover:text-amber-300">All On</button>
                    <span className="text-zinc-700">·</span>
                    <button type="button" onClick={() => setForm(f => ({...f, permissions: Object.fromEntries(ALL_PERMISSIONS.map(p => [p.key, false]))}))} className="text-zinc-500 hover:text-zinc-400">All Off</button>
                  </div>
                </div>

                {/* Single money-visibility switch — mirrors the user's request:
                    one "Can view money / valuations" checkbox driving prices,
                    costs and margins together. On for Owner/Admin by default. */}
                {(() => {
                  const moneyOn = !!(form.permissions.can_see_prices && form.permissions.can_see_costs && form.permissions.can_see_margins);
                  const setMoney = (on) => setForm(f => ({ ...f, permissions: { ...f.permissions, can_see_prices: on, can_see_costs: on, can_see_margins: on } }));
                  return (
                    <label data-testid="money-visibility-toggle"
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer mb-3 border transition-colors ${moneyOn ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'}`}>
                      <input type="checkbox" checked={moneyOn} onChange={e => setMoney(e.target.checked)} className="accent-emerald-500 w-4 h-4" data-testid="money-visibility-checkbox" />
                      <div>
                        <div className="text-zinc-200 text-sm font-medium">Can view money / valuations</div>
                        <div className="text-zinc-500 text-[11px]">Shows prices, costs, margins & stock values outside the finance pages. Off by default for non-admins.</div>
                      </div>
                    </label>
                  );
                })()}


                <div className="space-y-3">
                  {PERM_GROUPS.map(group => {
                    const onCount = group.items.filter(i => form.permissions[i.key]).length;
                    const allOn = onCount === group.items.length;
                    return (
                      <div key={group.id} className={`bg-zinc-950/40 border border-${group.color}-900/30 rounded-xl p-3`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className={`text-${group.color}-300 text-xs font-semibold uppercase tracking-wider`}>
                            {group.label} <span className="text-zinc-500 ml-1 normal-case">· {onCount}/{group.items.length}</span>
                          </h4>
                          <div className="flex gap-2 text-[10px]">
                            <button type="button" onClick={() => setAllInGroup(group.items, true)} className="text-zinc-400 hover:text-zinc-200">on</button>
                            <span className="text-zinc-700">·</span>
                            <button type="button" onClick={() => setAllInGroup(group.items, false)} className="text-zinc-500 hover:text-zinc-300">off</button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                          {group.items.map(p => (
                            <label key={p.key} data-testid={`perm-label-${p.key}`} className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${form.permissions[p.key] ? `bg-${group.color}-500/10 border border-${group.color}-500/30` : 'bg-zinc-900 border border-zinc-800 hover:border-zinc-700'}`}>
                              <input type="checkbox" checked={!!form.permissions[p.key]} onChange={() => togglePerm(p.key)} className={`accent-${group.color}-500 w-3.5 h-3.5`} data-testid={`perm-${p.key}`} />
                              <span className="text-zinc-300 text-[11px] truncate" title={p.label}>{p.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {form.role === 'custom' && (
                    <p className="text-[10px] text-amber-500/70 bg-amber-500/5 border border-amber-500/20 rounded-lg p-2">
                      💡 Custom mode — pick exact permissions. Tip: start by selecting a base role, then customize from there.
                    </p>
                  )}
                </div>
              </div>

              {error && <div className="bg-red-900/20 border border-red-800 rounded-xl p-3 text-red-300 text-xs">{error}</div>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm transition-colors">Cancel</button>
                <button data-testid="submit-user-btn" type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50 transition-colors">
                  {submitting ? 'Saving...' : (editing ? 'Update User' : 'Create User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />
    </div>
  );
}

// Per-user dashboard: visual tick/cross grid of what they can see.
// Privileged roles (owner / admin) see everything regardless of granular permissions.
function AccessMap({ user }) {
  const elevated = user.role === 'owner' || user.role === 'admin';
  const granular = user.permissions || {};
  const get = (mod) => elevated ? true : !!granular[mod];

  // Special rules from the codebase
  const financialView = elevated || user.role === 'manager';
  const categories = [
    {
      key: 'core', label: 'Core operations', items: [
        { key: 'dashboard',   label: 'Dashboard' },
        { key: 'sales',       label: 'Sales & Pipeline' },
        { key: 'purchases',   label: 'Purchases' },
        { key: 'inventory',   label: 'Inventory & Lots' },
        { key: 'production',  label: 'Production / Batches' },
        { key: 'rnd',         label: 'R&D' },
        { key: 'samples',     label: 'Samples' },
        { key: 'recipes',     label: 'Recipes (locked)' },
      ],
    },
    {
      key: 'finance', label: 'Finance', items: [
        { key: 'cashflow',    label: 'Cashflow', forceFin: true },
        { key: 'accounting',  label: 'Accounting', forceFin: true },
        { key: 'vouchers',    label: 'Vouchers', forceFin: true },
        { key: 'reports',     label: 'Reports', forceFin: true },
      ],
    },
    {
      key: 'people', label: 'People & ops', items: [
        { key: 'workforce',   label: 'Workforce' },
        { key: 'compliance',  label: 'Compliance' },
        { key: 'tasks',       label: 'Task Manager' },
        { key: 'notifications', label: 'Notifications' },
      ],
    },
    {
      key: 'admin', label: 'Admin only', items: [
        { key: 'user_management', label: 'User Management', adminOnly: true },
        { key: 'cost_variance',   label: 'Cost Variance', adminOnly: true, custom: () => elevated },
        { key: 'price_audit',     label: 'Price Audit Log', adminOnly: true, custom: () => elevated },
        { key: 'product_codes',   label: 'Product Codes', adminOnly: true, custom: () => elevated },
      ],
    },
    {
      key: 'finvis', label: 'Sensitive financial visibility', items: [
        { key: 'rev_widget', label: 'Revenue / GST KPIs', custom: () => financialView },
        { key: 'inv_value',  label: 'Inventory ₹ value', custom: () => financialView },
        { key: 'cash_bal',   label: 'Cash balance', custom: () => financialView },
        { key: 'rnd_prices', label: 'R&D ₹ rates', custom: () => financialView },
        { key: 'po_costs',   label: 'PO / Bill costs', custom: () => financialView },
      ],
    },
  ];

  const totalAllowed = categories.flatMap(c => c.items).filter(i => i.custom ? i.custom() : get(i.key)).length;
  const totalAll = categories.flatMap(c => c.items).length;

  return (
    <div data-testid={`access-map-${user.id}`} className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Shield size={14} className="text-sky-400" />
        <h3 className="font-heading font-semibold text-zinc-100 text-sm">Access map · {user.name}</h3>
        <span className="text-[11px] text-muted-foreground">
          {elevated ? 'Elevated role — full access' : `Sees ${totalAllowed} of ${totalAll} surfaces`}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {categories.map(cat => (
          <div key={cat.key} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 font-semibold">{cat.label}</div>
            <ul className="space-y-1">
              {cat.items.map(it => {
                const allowed = it.custom ? it.custom() : get(it.key);
                const lock = it.adminOnly && !elevated;
                return (
                  <li key={it.key} className="flex items-center gap-2 text-xs">
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                      allowed ? 'bg-green-500/20 border border-green-500/50' :
                      lock ? 'bg-zinc-800 border border-zinc-700' :
                      'bg-red-500/15 border border-red-500/40'
                    }`}>
                      {allowed ? <Check size={9} className="text-green-300" /> : <Minus size={9} className="text-zinc-500" />}
                    </span>
                    <span className={allowed ? 'text-zinc-100' : 'text-zinc-500 line-through decoration-zinc-700'}>
                      {it.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

